import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Flame, UtensilsCrossed, Globe2, Sparkles, ArrowRight, Quote,
  Coffee, Wine, Waves, Backpack, Clock, MapPin,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { PROPERTY_SERVICE_HOURS, HOURS_FAMILIES, SERVICE_ICON } from "@/lib/serviceHours";
import BrandMonogramEdge from "@/components/BrandMonogramEdge";
import PageHero from "@/components/PageHero";
import PageStickyNav from "@/components/PageStickyNav";

// Lucide icon registry for service kinds (string → component)
const SERVICE_ICON_MAP = { Coffee, UtensilsCrossed, Waves, Wine, Flame, Backpack };

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const HERO = "https://images.unsplash.com/photo-1604908554007-a30a554f3ee5?auto=format&fit=crop&w=2400&q=80";

const COPY = {
  overline: { en: "Gastronomy", fr: "Gastronomie", es: "Gastronomía" },
  title: {
    en: "A cuisine of the south — rooted, generous, open to the world.",
    fr: "Une cuisine du sud — enracinée, généreuse, ouverte au monde.",
    es: "Una cocina del sur — enraizada, generosa, abierta al mundo.",
  },
  sub: {
    en: "From a 12-hour-cooked mechoui to a mountain-trout fillet served beside the lake, Grup Xaluca's kitchens speak Moroccan first — and then welcome every guest in their own language.",
    fr: "Du méchoui cuit douze heures au filet de truite de montagne servi au bord du lac, les cuisines de Grup Xaluca parlent d'abord marocain — puis accueillent chaque hôte dans sa propre langue.",
    es: "Del mechoui cocido doce horas al filete de trucha de montaña servido a orillas del lago, las cocinas de Grup Xaluca hablan primero marroquí — y luego acogen a cada huésped en su propio idioma.",
  },
};

const PILLARS = [
  {
    icon: Flame,
    overline: { en: "Pillar 01", fr: "Pilier 01", es: "Pilar 01" },
    title: {
      en: "Authentic local Moroccan cuisine",
      fr: "Cuisine marocaine authentique et locale",
      es: "Cocina marroquí auténtica y local",
    },
    intro: {
      en: "Our kitchens are anchored in the south of Morocco. Recipes come from the families of Tafilalet, the Atlas valleys and the kasbah villages — and they are cooked the way they have always been cooked: slowly, generously, and with respect for the season.",
      fr: "Nos cuisines sont ancrées dans le sud du Maroc. Les recettes viennent des familles du Tafilalet, des vallées de l'Atlas et des villages-kasbah — et elles sont préparées comme elles l'ont toujours été : lentement, généreusement, dans le respect des saisons.",
      es: "Nuestras cocinas están ancladas en el sur de Marruecos. Las recetas vienen de las familias del Tafilalet, los valles del Atlas y los pueblos-kasbah — y se preparan como siempre se han preparado: despacio, con generosidad, respetando las estaciones.",
    },
    bullets: [
      {
        title: { en: "Mechoui cooked for 12 hours", fr: "Méchoui de 12 heures", es: "Mechoui de 12 horas" },
        text: {
          en: "Whole lamb, rubbed with cumin and saffron, sealed in a clay oven and slow-roasted overnight — served on copper trays at the heart of the Moroccan dining room.",
          fr: "Agneau entier, frotté au cumin et au safran, scellé en four d'argile et rôti toute la nuit — servi sur plateau de cuivre au cœur de la salle marocaine.",
          es: "Cordero entero, sazonado con comino y azafrán, sellado en horno de barro y asado lentamente toda la noche — servido en bandeja de cobre en el corazón del salón marroquí.",
        },
      },
      {
        title: { en: "Tagines & couscous of the families", fr: "Tajines & couscous des familles", es: "Tajines y cuscús de las familias" },
        text: {
          en: "Lamb with prunes, chicken with preserved lemon and olives, kefta with eggs, vegetable couscous on Friday — recipes carried by our cooks from their own grandmothers.",
          fr: "Agneau aux pruneaux, poulet au citron confit et olives, kefta aux œufs, couscous aux sept légumes le vendredi — des recettes que nos cuisinières tiennent de leurs grands-mères.",
          es: "Cordero con ciruelas, pollo al limón confitado y aceitunas, kefta con huevo, cuscús de siete verduras los viernes — recetas que nuestras cocineras heredan de sus propias abuelas.",
        },
      },
      {
        title: { en: "Pastilla, harira & street-soul classics", fr: "Pastilla, harira & classiques d'âme", es: "Pastilla, harira y clásicos del alma" },
        text: {
          en: "Pigeon pastilla dusted with cinnamon and icing sugar; harira simmered with lentils, lamb and tomato; warm khobz from the wood oven, drizzled with our own olive oil.",
          fr: "Pastilla au pigeon poudrée de cannelle et sucre glace ; harira mijotée aux lentilles, agneau et tomate ; khobz tiède du four à bois, arrosé de notre huile d'olive.",
          es: "Pastilla de pichón con canela y azúcar glas; harira a fuego lento con lentejas, cordero y tomate; khobz tibio del horno de leña, regado con nuestro aceite de oliva.",
        },
      },
      {
        title: { en: "Mint-tea ritual at every table", fr: "Rituel du thé à la menthe à chaque table", es: "Ritual del té a la menta en cada mesa" },
        text: {
          en: "Poured from height in three rounds — life, love and death — by a tea-master in white djellaba. The ritual closes every meal.",
          fr: "Versé en hauteur, en trois services — la vie, l'amour, la mort — par un maître du thé en djellaba blanche. Le rituel clôt chaque repas.",
          es: "Servido en alto, en tres rondas — la vida, el amor, la muerte — por un maestro del té con chilaba blanca. El ritual cierra cada comida.",
        },
      },
    ],
    image: "https://images.unsplash.com/photo-1541518763669-27fef04b14ea?auto=format&fit=crop&w=1800&q=80",
  },
  {
    icon: Globe2,
    overline: { en: "Pillar 02", fr: "Pilier 02", es: "Pilar 02" },
    title: {
      en: "Thoughtful adaptation for international guests",
      fr: "Adaptation attentive pour les voyageurs du monde",
      es: "Adaptación atenta para los viajeros del mundo",
    },
    intro: {
      en: "Travellers come to us from forty countries. We never disguise our food — but we listen. Salt is dialled, spice can be tamed, and our chefs trained in Marrakech, Paris and Madrid weave Mediterranean and continental moments into every dinner.",
      fr: "Nos voyageurs viennent de quarante pays. Nous ne déguisons jamais notre cuisine — mais nous écoutons. Le sel se mesure, les épices s'adoucissent à la demande, et nos chefs formés à Marrakech, Paris et Madrid composent des moments méditerranéens et continentaux à chaque dîner.",
      es: "Nuestros viajeros vienen de cuarenta países. Nunca disfrazamos nuestra cocina — pero escuchamos. La sal se mide, las especias se atemperan a petición, y nuestros chefs formados en Marrakech, París y Madrid componen momentos mediterráneos y continentales en cada cena.",
    },
    bullets: [
      {
        title: { en: "Mediterranean tasting menu", fr: "Menu dégustation méditerranéen", es: "Menú degustación mediterráneo" },
        text: {
          en: "A nightly four-course menu in the International dining room — grilled sea bream from Agadir, lamb cutlets with rosemary, saffron risotto, citrus panna cotta.",
          fr: "Un menu quatre services chaque soir dans la salle internationale — dorade grillée d'Agadir, côtelettes d'agneau au romarin, risotto au safran, panna cotta aux agrumes.",
          es: "Un menú de cuatro tiempos cada noche en el salón internacional — dorada a la parrilla de Agadir, costillas de cordero al romero, risotto al azafrán, panna cotta de cítricos.",
        },
      },
      {
        title: { en: "Vegetarian, vegan & gluten-free", fr: "Végétarien, végan & sans gluten", es: "Vegetariano, vegano y sin gluten" },
        text: {
          en: "Every menu carries clearly marked vegetarian, vegan and gluten-free dishes — and our chef will design a custom plate within minutes if your dietary need is more specific.",
          fr: "Chaque carte propose des plats végétariens, végans et sans gluten clairement signalés — et notre chef compose en quelques minutes une assiette sur mesure si votre besoin est plus particulier.",
          es: "Cada carta incluye platos vegetarianos, veganos y sin gluten claramente señalados — y nuestro chef compone en minutos un plato a medida si su necesidad es más específica.",
        },
      },
      {
        title: { en: "Children's table", fr: "Table des enfants", es: "Mesa de los niños" },
        text: {
          en: "Mild tagines, hand-cut pasta, grilled chicken, fruit skewers — and a kitchen door always open to a curious young palate.",
          fr: "Tajines doux, pâtes maison, poulet grillé, brochettes de fruits — et une porte de cuisine toujours ouverte à un jeune palais curieux.",
          es: "Tajines suaves, pasta artesanal, pollo a la parrilla, brochetas de fruta — y una puerta de cocina siempre abierta a un paladar joven curioso.",
        },
      },
      {
        title: { en: "Halal, kosher & allergy-aware kitchen", fr: "Cuisine halal, casher et allergènes maîtrisés", es: "Cocina halal, kosher y consciente de alergias" },
        text: {
          en: "All our kitchens are 100 % halal. Kosher meals are arranged on request with 48 hours of notice. Allergen information is held by every head waiter.",
          fr: "Toutes nos cuisines sont 100 % halal. Les repas casher s'organisent sur demande avec 48 h de préavis. Les informations sur les allergènes sont tenues par chaque maître d'hôtel.",
          es: "Todas nuestras cocinas son 100 % halal. Las comidas kosher se organizan a petición con 48 h de antelación. La información de alérgenos está en manos de cada maître.",
        },
      },
    ],
    image: "https://images.unsplash.com/photo-1559339352-11d035aa65de?auto=format&fit=crop&w=1800&q=80",
  },
  {
    icon: UtensilsCrossed,
    overline: { en: "Pillar 03", fr: "Pilier 03", es: "Pilar 03" },
    title: {
      en: "A diverse culinary experience — buffet, à la carte and signature dinners",
      fr: "Une expérience culinaire diverse — buffet, à la carte et dîners signature",
      es: "Una experiencia culinaria diversa — buffet, à la carte y cenas de autor",
    },
    intro: {
      en: "Every evening at a Xaluca property unfolds in a different way. A long buffet under brass lanterns. A quiet à-la-carte dinner on a private terrace. A nomad feast inside the dunes. The choice — and the rhythm — is yours.",
      fr: "Chaque soir dans un établissement Xaluca s'écrit différemment. Un long buffet sous lanternes de laiton. Un dîner à la carte sur une terrasse privée. Un festin nomade au cœur des dunes. Le choix — et le rythme — vous appartiennent.",
      es: "Cada noche en un establecimiento Xaluca se escribe de otra manera. Un largo buffet bajo lámparas de latón. Una cena à la carte en una terraza privada. Un festín nómada en el corazón de las dunas. La elección — y el ritmo — son suyos.",
    },
    bullets: [
      {
        title: { en: "The grand buffet", fr: "Le grand buffet", es: "El gran buffet" },
        text: {
          en: "Live cooking stations every evening — tagines, grilled meats, pasta of the day, salad island, pâtissier corner. The Moroccan and International dining rooms each seat 350.",
          fr: "Stations de cuisine en direct chaque soir — tajines, grillades, pâtes du jour, île salades, coin pâtissier. Les salles marocaine et internationale accueillent chacune 350 couverts.",
          es: "Estaciones de cocina en vivo cada noche — tajines, parrilladas, pasta del día, isla de ensaladas, rincón pastelero. Los salones marroquí e internacional acogen 350 cubiertos cada uno.",
        },
      },
      {
        title: { en: "À la carte by the chef", fr: "À la carte du chef", es: "À la carte del chef" },
        text: {
          en: "Reserved tables for an intimate dinner — a curated card of six starters, eight mains and four desserts, refreshed every season, paired with Moroccan and European wines.",
          fr: "Tables réservées pour un dîner intime — une carte choisie de six entrées, huit plats et quatre desserts, renouvelée à chaque saison, accordée aux vins marocains et européens.",
          es: "Mesas reservadas para una cena íntima — una carta cuidada de seis entrantes, ocho principales y cuatro postres, renovada cada estación, acompañada de vinos marroquíes y europeos.",
        },
      },
      {
        title: { en: "Poolside lunch & terrace breakfast", fr: "Déjeuner en bord de piscine & petit-déjeuner en terrasse", es: "Almuerzo junto a la piscina y desayuno en terraza" },
        text: {
          en: "Light lunches by the lagoon pool — grilled fish, salads, juices and ice-cold mocktails. A slow buffet breakfast on the open terrace, from 06:30 until late.",
          fr: "Déjeuners légers en bord de piscine lagon — poissons grillés, salades, jus, mocktails glacés. Petit-déjeuner buffet lent sur la terrasse ouverte, de 6 h 30 jusque tard.",
          es: "Almuerzos ligeros junto a la piscina laguna — pescados a la parrilla, ensaladas, zumos, mocktails muy fríos. Desayuno buffet sin prisas en la terraza abierta, desde las 6:30 hasta tarde.",
        },
      },
      {
        title: { en: "Bivouac dinners under the stars", fr: "Dîners-bivouac sous les étoiles", es: "Cenas-bivac bajo las estrellas" },
        text: {
          en: "At our desert camps — Erg Chebbi, Merdani, La Belle Étoile — dinner is served on woven mats: tanjia of lamb cooked in the embers, vegetable couscous, mint tea and Berber music around the fire.",
          fr: "Dans nos camps du désert — Erg Chebbi, Merdani, La Belle Étoile — le dîner se prend sur tapis tissés : tanjia d'agneau aux braises, couscous, thé à la menthe et musique berbère autour du feu.",
          es: "En nuestros campamentos del desierto — Erg Chebbi, Merdani, La Belle Étoile — la cena se sirve sobre alfombras tejidas: tanjia de cordero a las brasas, cuscús, té a la menta y música bereber alrededor del fuego.",
        },
      },
    ],
    image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1800&q=80",
  },
];

const VENUES = [
  {
    title: { en: "Moroccan dining room — Arfoud", fr: "Salle marocaine — Arfoud", es: "Salón marroquí — Arfoud" },
    capacity: { en: "350 covers", fr: "350 couverts", es: "350 cubiertos" },
    image: "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/qrtoq576_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-5.jpg",
  },
  {
    title: { en: "International dining room — Arfoud", fr: "Salle internationale — Arfoud", es: "Salón internacional — Arfoud" },
    capacity: { en: "350 covers", fr: "350 couverts", es: "350 cubiertos" },
    image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1600&q=80",
  },
  {
    title: { en: "Bar Tataoui & Bar Bab Sahra", fr: "Bar Tataoui & Bar Bab Sahra", es: "Bar Tataoui y Bar Bab Sahra" },
    capacity: { en: "Open until 01:00", fr: "Ouvert jusqu'à 1 h", es: "Abierto hasta la 1:00" },
    image: "https://images.unsplash.com/photo-1470337458703-46ad1756a187?auto=format&fit=crop&w=1600&q=80",
  },
  {
    title: { en: "Lakeside restaurant — Sidi Ali", fr: "Restaurant du lac — Sidi Ali", es: "Restaurante del lago — Sidi Ali" },
    capacity: { en: "120 covers · trout & cedar wood", fr: "120 couverts · truite & cèdre", es: "120 cubiertos · trucha y cedro" },
    image: "https://images.unsplash.com/photo-1551782450-a2132b4ba21d?auto=format&fit=crop&w=1600&q=80",
  },
  {
    title: { en: "Bivouac fire-circle dinner", fr: "Dîner autour du feu en bivouac", es: "Cena en círculo de fuego en bivac" },
    capacity: { en: "Erg Chebbi · Merdani", fr: "Erg Chebbi · Merdani", es: "Erg Chebbi · Merdani" },
    image: "https://images.unsplash.com/photo-1518684079-3c830dcef090?auto=format&fit=crop&w=1600&q=80",
  },
  {
    title: { en: "Poolside terrace lunch", fr: "Déjeuner terrasse piscine", es: "Almuerzo en la terraza de la piscina" },
    capacity: { en: "All Xaluca hotels", fr: "Tous les hôtels Xaluca", es: "Todos los hoteles Xaluca" },
    image: "https://images.unsplash.com/photo-1559339352-11d035aa65de?auto=format&fit=crop&w=1600&q=80",
  },
];

const CHEF_QUOTE = {
  en: "“Hospitality, for us, begins at the table. We cook the food of our grandmothers — and we set it down in front of every guest as if they were our own.”",
  fr: "« L'hospitalité, pour nous, commence à table. Nous cuisinons la nourriture de nos grands-mères — et nous la posons devant chaque hôte comme s'il était l'un des nôtres. »",
  es: "«La hospitalidad, para nosotros, empieza en la mesa. Cocinamos la comida de nuestras abuelas — y la ponemos delante de cada huésped como si fuera uno de los nuestros.»",
};
const CHEF_NAME = {
  en: "— Chef Hassan El Aouad, Executive Chef · Grup Xaluca",
  fr: "— Chef Hassan El Aouad, Chef exécutif · Grup Xaluca",
  es: "— Chef Hassan El Aouad, Chef ejecutivo · Grup Xaluca",
};

const SERVICES_INFO = [
  {
    label: { en: "Breakfast", fr: "Petit-déjeuner", es: "Desayuno" },
    hours: { en: "06:30 – 10:30", fr: "6 h 30 – 10 h 30", es: "6:30 – 10:30" },
  },
  {
    label: { en: "Lunch / Poolside", fr: "Déjeuner / Piscine", es: "Almuerzo / Piscina" },
    hours: { en: "12:30 – 15:00", fr: "12 h 30 – 15 h", es: "12:30 – 15:00" },
  },
  {
    label: { en: "Dinner buffet", fr: "Buffet du soir", es: "Buffet de la noche" },
    hours: { en: "19:30 – 22:30", fr: "19 h 30 – 22 h 30", es: "19:30 – 22:30" },
  },
  {
    label: { en: "À la carte", fr: "À la carte", es: "À la carte" },
    hours: { en: "On reservation · 19:30 – 22:00", fr: "Sur réservation · 19 h 30 – 22 h", es: "Bajo reserva · 19:30 – 22:00" },
  },
  {
    label: { en: "Bars", fr: "Bars", es: "Bares" },
    hours: { en: "11:00 – 01:00", fr: "11 h – 1 h", es: "11:00 – 1:00" },
  },
];

const GastronomyPage = () => {
  const { lang } = useLanguage();
  return (
    <div data-testid="page-gastronomy">
      {/* HERO — multi-slide (video first, then gastronomy photography) */}
      <section data-testid="gastronomy-hero">
        <PageHero
          images={[
            HERO,
            "https://images.unsplash.com/photo-1541518763669-27fef04b14ea?auto=format&fit=crop&w=2400&q=80",
            "https://images.unsplash.com/photo-1543352634-99a5d50ae78e?auto=format&fit=crop&w=2400&q=80",
          ]}
          alt="Gastronomy · Grup Xaluca"
          scope="page.gastronomy"
          overline={pick(COPY.overline, lang)}
          title={pick(COPY.title, lang)}
          sub={pick(COPY.sub, lang)}
        />
      </section>

      <PageStickyNav
        items={[
          { id: "gastronomy-pillars", label: { en: "Pillars", fr: "Piliers", es: "Pilares" } },
          { id: "gastronomy-chef", label: { en: "Chef", fr: "Chef", es: "Chef" } },
          { id: "gastronomy-venues", label: { en: "Venues", fr: "Adresses", es: "Locales" } },
          { id: "gastronomy-hours", label: { en: "Hours", fr: "Horaires", es: "Horarios" } },
          { id: "gastronomy-property-hours-section", label: { en: "Properties", fr: "Adresses", es: "Propiedades" } },
          { id: "gastronomy-cta", label: { en: "Contact", fr: "Contact", es: "Contacto" } },
        ]}
      />

      {/* PILLARS */}
      <div id="gastronomy-pillars" className="scroll-mt-32">
      {PILLARS.map((p, i) => {
        const Icon = p.icon;
        const reversed = i % 2 === 1;
        return (
          <section
            key={`pillar-${p.title.en}`}
            className={`py-20 md:py-28 ${i % 2 === 1 ? "bg-[#F2EBE1]/40" : ""}`}
            data-testid={`gastronomy-pillar-${i}`}
            data-cms-scope={`gastronomy.pillar.${i}`}
          >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <div className={`grid lg:grid-cols-12 gap-10 lg:gap-16 items-start ${reversed ? "lg:[direction:rtl]" : ""}`}>
                <div className={`lg:col-span-7 ${reversed ? "lg:[direction:ltr]" : ""}`}>
                  <div className="flex items-center gap-3">
                    <Icon className="w-6 h-6 text-[#C16542]" strokeWidth={1.5} />
                    <p className="overline">{pick(p.overline, lang)}</p>
                  </div>
                  <h2 className="font-serif-x mt-4 text-4xl md:text-5xl tracking-tight leading-tight text-[#2C2621]">
                    {pick(p.title, lang)}
                  </h2>
                  <p className="mt-6 text-lg leading-relaxed text-[#3C342D]">
                    {pick(p.intro, lang)}
                  </p>
                  <ul className="mt-10 space-y-7">
                    {p.bullets.map((b, j) => (
                      <li
                        key={`pillar-${p.title.en}-bullet-${b.title.en}`}
                        className="flex gap-5"
                        data-testid={`gastronomy-pillar-${i}-bullet-${j}`}
                      >
                        <span className="font-serif-x text-2xl text-[#C16542] leading-none w-10 shrink-0 mt-1">
                          {String(j + 1).padStart(2, "0")}
                        </span>
                        <div>
                          <h3 className="font-serif-x text-xl md:text-2xl text-[#2C2621] leading-tight">
                            {pick(b.title, lang)}
                          </h3>
                          <p className="mt-2 text-[15px] text-[#5C5248] leading-relaxed">
                            {pick(b.text, lang)}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={`lg:col-span-5 ${reversed ? "lg:[direction:ltr]" : ""}`}>
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <img src={p.image} alt={pick(p.title, lang)} className="w-full h-full object-cover" loading="lazy" decoding="async" data-cms-role="card.image"/>
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/40 to-transparent" />
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}
      </div>

      {/* CHEF QUOTE */}
      <section id="gastronomy-chef" className="py-20 md:py-28 bg-[#1A1513] text-[#FDFBF7] scroll-mt-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Quote className="w-8 h-8 mx-auto text-[#D4A373]" strokeWidth={1.5} />
          <p className="mt-6 font-serif-x text-3xl md:text-4xl leading-snug tracking-tight">
            {pick(CHEF_QUOTE, lang)}
          </p>
          <p className="mt-6 text-[11px] tracking-[0.3em] uppercase text-[#FDFBF7]/70">
            {pick(CHEF_NAME, lang)}
          </p>
        </div>
      </section>

      {/* VENUES */}
      <section id="gastronomy-venues" className="py-20 md:py-28 scroll-mt-32" data-testid="gastronomy-venues">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <p className="overline">{pick({ en: "Tables of the group", fr: "Tables du groupe", es: "Mesas del grupo" }, lang)}</p>
              <h2 className="font-serif-x mt-3 text-4xl md:text-5xl tracking-tight leading-tight text-[#2C2621]">
                {pick({ en: "Six ways to dine across the south", fr: "Six manières de dîner dans le sud", es: "Seis maneras de cenar en el sur" }, lang)}
              </h2>
            </div>
            <Link
              to="/hotels"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase border-b border-[#2C2621]/40 pb-1 hover:border-[#C16542] hover:text-[#C16542] transition-colors w-fit"
              data-testid="gastronomy-link-hotels"
            >
              {pick({ en: "Discover the hotels", fr: "Découvrir les hôtels", es: "Descubrir los hoteles" }, lang)}
              <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {VENUES.map((v, i) => (
              <article key={`venue-${v.title.en}`} className="group" data-testid={`gastronomy-venue-${i}`} data-cms-scope={`gastronomy.venue.${i}`}>
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={v.image}
                    alt={pick(v.title, lang)}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" decoding="async"
                    data-cms-role="card.image"/>
                </div>
                <h3 className="font-serif-x text-2xl mt-5 leading-tight text-[#2C2621]">
                  {pick(v.title, lang)}
                </h3>
                <p className="mt-1 text-[11px] tracking-[0.25em] uppercase text-[#5C5248]">
                  {pick(v.capacity, lang)}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE HOURS */}
      <section id="gastronomy-hours" className="py-20 md:py-24 bg-[#F2EBE1]/50 scroll-mt-32">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-10 items-end mb-10">
            <div className="md:col-span-7">
              <p className="overline">{pick({ en: "Service hours", fr: "Horaires de service", es: "Horarios de servicio" }, lang)}</p>
              <h2 className="font-serif-x mt-3 text-3xl md:text-4xl tracking-tight leading-tight text-[#2C2621]">
                {pick({ en: "From the first coffee to the last poured tea.", fr: "Du premier café au dernier thé servi.", es: "Del primer café al último té servido." }, lang)}
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-[15px] text-[#5C5248] leading-relaxed">
                {pick({
                  en: "Indicative timings — each Xaluca hotel adapts its rhythm to the season, the property and your stay. Our concierge is happy to plan a private dinner outside these hours.",
                  fr: "Horaires indicatifs — chaque hôtel Xaluca adapte son rythme à la saison, au lieu et à votre séjour. Notre conciergerie organise volontiers un dîner privé en dehors de ces créneaux.",
                  es: "Horarios indicativos — cada hotel Xaluca adapta su ritmo a la temporada, al lugar y a su estancia. Nuestra conserjería organiza con gusto una cena privada fuera de estos horarios.",
                }, lang)}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-[#2C2621]/10 border border-[#2C2621]/10">
            {SERVICES_INFO.map((s, i) => (
              <div key={`hours-${s.label.en}`} className="bg-[#FDFBF7] p-6" data-testid={`gastronomy-hours-${i}`}>
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#C16542]">
                  {pick(s.label, lang)}
                </p>
                <p className="mt-3 font-serif-x text-xl text-[#2C2621] leading-tight">
                  {pick(s.hours, lang)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PER-PROPERTY SERVICE HOURS — granular hours for every hotel, camp & refuge */}
      <PropertyServiceHoursSection lang={lang} />

      {/* CTA */}
      <section id="gastronomy-cta" className="py-24 md:py-32 bg-[#1A1513] text-[#FDFBF7] scroll-mt-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Sparkles className="w-7 h-7 mx-auto text-[#D4A373]" strokeWidth={1.5} />
          <h2 className="font-serif-x mt-6 text-4xl md:text-5xl tracking-tight leading-tight">
            {pick({
              en: "Reserve a table — or design a private dinner with our chef.",
              fr: "Réserver une table — ou composer un dîner privé avec notre chef.",
              es: "Reserve una mesa — o componga una cena privada con nuestro chef.",
            }, lang)}
          </h2>
          <p className="mt-5 text-[#FDFBF7]/75 text-lg leading-relaxed">
            {pick({
              en: "From a lakeside lunch in Sidi Ali to a bivouac feast in the Erg Chebbi dunes, our concierge tailors the moment to you.",
              fr: "D'un déjeuner au bord du lac de Sidi Ali à un festin nomade dans les dunes de l'Erg Chebbi, notre conciergerie compose le moment autour de vous.",
              es: "De un almuerzo a orillas del lago de Sidi Ali a un festín nómada en las dunas del Erg Chebbi, nuestra conserjería compone el momento a su medida.",
            }, lang)}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-[#C16542] text-[#FDFBF7] px-7 py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-[#D4A373] hover:text-[#1A1513] transition-colors"
              data-testid="gastronomy-cta-contact"
            >
              {pick({ en: "Speak to the concierge", fr: "Parler à la conciergerie", es: "Hablar con conserjería" }, lang)}
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.6} />
            </Link>
            <Link
              to="/booking"
              className="inline-flex items-center justify-center gap-2 border border-[#FDFBF7]/40 px-7 py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-[#FDFBF7] hover:text-[#1A1513] transition-colors"
              data-testid="gastronomy-cta-booking"
            >
              {pick({ en: "Reserve a stay", fr: "Réserver un séjour", es: "Reservar una estancia" }, lang)}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GastronomyPage;

// ────────────────────────────────────────────────────────────────────────
// PROPERTY-LEVEL SERVICE HOURS
// Granular per-hotel / per-camp / per-refuge F&B timings.
// Includes a family filter (all / hotel / bivouac / refuge) so guests can
// jump straight to the property type they're staying in.

const FAMILY_BADGE = {
  hotel: { en: "Kasbah & hotel", fr: "Kasbah & hôtel", es: "Kasbah y hotel" },
  bivouac: { en: "Bivouac & camp", fr: "Bivouac & camp", es: "Vivac y campamento" },
  refuge: { en: "Mountain refuge", fr: "Refuge de montagne", es: "Refugio de montaña" },
};

const FAMILY_ACCENT = {
  hotel: "#C16542",
  bivouac: "#A07042",
  refuge: "#5A6B4F",
};

const PropertyServiceHoursSection = ({ lang }) => {
  const [family, setFamily] = useState("all");
  const filtered = useMemo(
    () => (family === "all"
      ? PROPERTY_SERVICE_HOURS
      : PROPERTY_SERVICE_HOURS.filter((p) => p.category === family)),
    [family]
  );

  return (
    <section
      id="gastronomy-property-hours-section"
      className="py-24 md:py-32 bg-[#FDFBF7] scroll-mt-32"
      data-testid="gastronomy-property-hours-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="grid md:grid-cols-12 gap-8 lg:gap-12 mb-12 md:mb-16">
          <div className="md:col-span-7">
            <p className="overline">
              {pick({ en: "By property", fr: "Adresse par adresse", es: "Dirección a dirección" }, lang)}
            </p>
            <h2
              className="mt-5 font-serif-x text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#2C2621]"
              data-testid="gastronomy-property-hours-title"
            >
              {pick({
                en: "Every table, every bar — across every Xaluca address.",
                fr: "Chaque table, chaque bar — dans chaque adresse Xaluca.",
                es: "Cada mesa, cada bar — en cada dirección Xaluca.",
              }, lang)}
            </h2>
          </div>
          <div className="md:col-span-5 md:pt-3">
            <p className="text-base md:text-lg leading-relaxed text-[#3C342D]">
              {pick({
                en: "From a 06:30 ksar breakfast to a fire-circle dinner under the dune — here are the exact hours we keep at every hotel, camp and refuge in the south.",
                fr: "Du petit-déjeuner d'un ksar à 06 h 30 au dîner en cercle de feu sous la dune — voici les horaires exacts de chaque hôtel, camp et refuge du sud.",
                es: "Desde un desayuno en el ksar a las 06:30 hasta una cena en círculo de fuego bajo la duna — aquí están los horarios exactos de cada hotel, campamento y refugio del sur.",
              }, lang)}
            </p>
          </div>
        </div>

        {/* Family filter chips */}
        <div className="flex flex-wrap gap-2 mb-10 md:mb-14" role="tablist" data-testid="gastronomy-hours-filters">
          {HOURS_FAMILIES.map((f) => (
            <button
              key={`hours-fam-${f.id}`}
              type="button"
              onClick={() => setFamily(f.id)}
              role="tab"
              aria-selected={family === f.id}
              className={`text-[10px] tracking-[0.25em] uppercase px-4 py-2.5 border transition-colors duration-200 ${
                family === f.id
                  ? "bg-[#2C2621] text-[#FDFBF7] border-[#2C2621]"
                  : "border-[#2C2621]/15 text-[#5C5248] hover:border-[#C16542] hover:text-[#C16542]"
              }`}
              data-testid={`gastronomy-hours-filter-${f.id}`}
            >
              {pick(f.label, lang)}
            </button>
          ))}
        </div>

        {/* Property cards — 2-col editorial grid */}
        <div
          className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8"
          data-testid="gastronomy-property-hours-grid"
        >
          {filtered.map((prop) => {
            const accent = FAMILY_ACCENT[prop.category] || "#C16542";
            return (
              <article
                key={`hours-${prop.slug}`}
                className="bg-white border border-[#2C2621]/10 hover:border-[#2C2621]/25 transition-colors duration-300 p-8 md:p-10 flex flex-col"
                data-testid={`gastronomy-hours-property-${prop.slug}`}
              >
                {/* Property heading */}
                <div className="flex items-start justify-between gap-4 pb-6 border-b border-[#2C2621]/10">
                  <div className="min-w-0">
                    <p
                      className="text-[10px] tracking-[0.3em] uppercase"
                      style={{ color: accent }}
                    >
                      {pick(FAMILY_BADGE[prop.category], lang)}
                    </p>
                    <h3 className="font-serif-x mt-2 text-2xl md:text-3xl leading-[1.1] text-[#2C2621]">
                      {pick(prop.name, lang)}
                    </h3>
                    <p className="mt-2 flex items-center gap-2 text-xs tracking-wide text-[#5C5248]">
                      <MapPin className="w-3 h-3" strokeWidth={1.5} style={{ color: accent }} />
                      {pick(prop.location, lang)}
                    </p>
                  </div>
                  <Link
                    to={`/hotels/${prop.slug}`}
                    className="hidden md:inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase border-b border-[#2C2621]/30 pb-0.5 text-[#2C2621] hover:border-[#C16542] hover:text-[#C16542] transition-colors"
                    data-testid={`gastronomy-hours-property-${prop.slug}-link`}
                  >
                    {pick({ en: "Visit", fr: "Visiter", es: "Visitar" }, lang)}
                    <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
                  </Link>
                </div>

                {/* Service list */}
                <ul className="mt-6 divide-y divide-[#2C2621]/8" data-testid={`gastronomy-hours-services-${prop.slug}`}>
                  {prop.services.map((svc) => {
                    const Icon = SERVICE_ICON_MAP[SERVICE_ICON[svc.kind]] || Clock;
                    return (
                      <li
                        key={`svc-${prop.slug}-${svc.kind}`}
                        className="py-4 grid grid-cols-12 gap-3 items-start"
                      >
                        <div className="col-span-1 pt-0.5">
                          <Icon className="w-4 h-4" strokeWidth={1.4} style={{ color: accent }} />
                        </div>
                        <div className="col-span-7">
                          <p className="text-[13px] tracking-wide font-medium text-[#2C2621]">
                            {pick(svc.label, lang)}
                          </p>
                          {svc.venue && (
                            <p className="mt-0.5 text-[12px] text-[#5C5248] italic">
                              {pick(svc.venue, lang)}
                            </p>
                          )}
                          {svc.note && (
                            <p className="mt-1 text-[11px] text-[#5C5248]/85">
                              {pick(svc.note, lang)}
                            </p>
                          )}
                        </div>
                        <div className="col-span-4 text-right">
                          <span className="font-serif-x text-base md:text-lg tabular-nums text-[#2C2621] tracking-tight whitespace-nowrap">
                            {svc.hours}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>

                {/* Property-wide note (seasonal etc.) */}
                {prop.note && (
                  <p
                    className="mt-6 pt-5 border-t border-[#2C2621]/8 text-[12px] text-[#5C5248] italic leading-relaxed"
                    data-testid={`gastronomy-hours-property-${prop.slug}-note`}
                  >
                    {pick(prop.note, lang)}
                  </p>
                )}
              </article>
            );
          })}
        </div>

        {/* Footnote */}
        <p
          className="mt-12 text-center text-[11px] tracking-[0.2em] uppercase text-[#5C5248]"
          data-testid="gastronomy-hours-footnote"
        >
          {pick({
            en: "Hours shown in Moroccan local time · Subject to seasonal variation · Private dining available on request.",
            fr: "Horaires en heure locale marocaine · Variations saisonnières possibles · Dîners privés sur demande.",
            es: "Horarios en hora local marroquí · Posibles variaciones estacionales · Cenas privadas bajo petición.",
          }, lang)}
        </p>
      </div>
    </section>
  );
};
